import {
  CascadiaColor,
  CascadiaStandardIcon,
  CascadiaTypographyStyle,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { mergeProps } from "react-aria";
import {
  MenuItem as AriaMenuItem,
  MenuItemProps as AriaMenuItemProps,
} from "react-aria-components";

import { Icon } from "../../../foundations/icons/Icon/Icon";
import { Text } from "../../../foundations/typography/Text";

export interface MenuItemProps extends AriaMenuItemProps {
  icon?: CascadiaStandardIcon;
  iconPosition?: "left" | "right";
  iconColor?: CascadiaColor;
  textStyle?: CascadiaTypographyStyle;
  testID?: string;
  iconTestID?: string;
  textColor?: CascadiaColor;
  padding?: string;
  minWidth?: string;
}

export function MenuItem(props: MenuItemProps) {
  const {
    children,
    icon,
    iconPosition = "left",
    testID = "menu-item",
    iconColor = "icon-base",
    textStyle = "input/text-field",
    textValue = typeof children === "string" ? children : undefined,
    textColor = "text-base",
    minWidth,
    padding,
    iconTestID,
    ...menuItemProps
  } = props;

  return (
    <AriaMenuItem
      {...mergeProps(menuItemProps, {
        className: css(styles.menuItem, {
          minWidth,
          padding: padding ?? `${"var(--space-2)"} ${"var(--space-3)"}`,
          "&[data-disabled]": {
            cursor: "not-allowed",
            color: "var(--text-disabled)",
          },
        }),
        textValue,
        "data-testid": testID,
      })}
    >
      {(renderProps) => {
        return (
          <>
            {icon && iconPosition === "left" && (
              <div
                className={css(styles.iconWrapper, {
                  marginRight: "var(--space-2-5)",
                })}
              >
                <Icon
                  color={renderProps.isDisabled ? "text-disabled" : iconColor}
                  name={icon}
                  testID={iconTestID}
                />
              </div>
            )}
            <Text
              style={textStyle}
              color={renderProps.isDisabled ? "text-disabled" : textColor}
              className={css({ flex: 1 })}
            >
              {typeof props.children === "function"
                ? props.children(renderProps)
                : props.children}
            </Text>
            {icon && iconPosition === "right" && (
              <div
                className={css(styles.iconWrapper, {
                  marginLeft: "var(--space-2-5)",
                })}
              >
                <Icon
                  color={iconColor ?? "icon-base"}
                  name={icon}
                  testID={iconTestID}
                />
              </div>
            )}
          </>
        );
      }}
    </AriaMenuItem>
  );
}

const styles = {
  menuItem: css({
    outline: "none",
    marginLeft: "var(--space-1)",
    marginRight: "var(--space-1)",
    display: "flex",
    alignItems: "center",
    borderRadius: "var(--border-radius-2)",
    cursor: "pointer",
    textDecoration: "none",
    "&[data-hovered], &[data-focus-visible]": {
      background: "var(--background-ghost-hover)",
    },
    "&[data-selected]": {
      background: "var(--background-ghost-hover)",
    },
  }),
  iconWrapper: css({
    display: "flex",
    alignItems: "center",
    padding: "var(--space-1)",
  }),
};
